"use client";

import { CheckCheck } from "lucide-react";
import { cn } from "@/lib/utils";

/** Notification type → the leading status dot colour (Figma 213:221462). */
type NotificationType = "confirmed" | "upcoming" | "canceled" | "lead";

const DOT: Record<NotificationType, string> = {
  confirmed: "bg-primary-500",
  upcoming: "bg-success-500",
  canceled: "bg-error-500",
  lead: "bg-success-300",
};

interface NotificationItem {
  id: string;
  type: NotificationType;
  title: string;
  subtitle: string;
  time: string;
}

interface NotificationGroup {
  label: string;
  items: NotificationItem[];
}

/** Sample notifications (UI-only — no backend yet). */
const GROUPS: NotificationGroup[] = [
  {
    label: "Today",
    items: [
      {
        id: "n1",
        type: "confirmed",
        title: "Booking Confirmed",
        subtitle: "Sunrise Media just confirm booking",
        time: "2h ago",
      },
      {
        id: "n2",
        type: "upcoming",
        title: "Upcoming Event",
        subtitle: "Sunrise Media",
        time: "10m ago",
      },
      {
        id: "n3",
        type: "canceled",
        title: "Booking Canceled",
        subtitle: "Musa just cancelled - Wedding…",
        time: "2h ago",
      },
    ],
  },
  {
    label: "Yesterday",
    items: [
      {
        id: "n4",
        type: "lead",
        title: "New Lead",
        subtitle: "Sadid - Birthday Party",
        time: "24h ago",
      },
    ],
  },
];

export interface NotificationPanelProps {
  /** Unread count shown in the header. */
  unreadCount?: number;
  onMarkAllRead?: () => void;
}

/**
 * Notifications dropdown opened from the top-nav bell (Figma 213:221462) — a
 * header with the unread count + "Mark all as read", then date-grouped rows
 * (a type-coloured dot, title, subtitle, and relative time). UI-only for now.
 */
export function NotificationPanel({
  unreadCount = 3,
  onMarkAllRead,
}: NotificationPanelProps) {
  return (
    <div className="flex max-h-[70vh] w-[383px] max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-xl border border-stroke-soft-200 bg-white shadow-md">
      {/* Header. */}
      <div className="flex items-center justify-between gap-2 border-stroke-soft-200 border-b px-4 py-3">
        <span className="label-md text-text-primary">
          You have {unreadCount} new notifications
        </span>
        <button
          type="button"
          onClick={onMarkAllRead}
          className="label-sm flex shrink-0 items-center gap-1 text-primary-500 transition-colors hover:text-primary-600"
        >
          <CheckCheck className="size-4" />
          Mark all as read
        </button>
      </div>

      {/* Date-grouped list. */}
      <div className="flex flex-col overflow-y-auto">
        {GROUPS.map((group) => (
          <div key={group.label} className="flex flex-col">
            <p className="px-4 pt-3 pb-1 label-sm text-text-secondary">
              {group.label}
            </p>
            {group.items.map((n) => (
              <div
                key={n.id}
                className="flex items-start gap-3 border-stroke-soft-200 border-b px-4 py-3"
              >
                <span
                  className={cn(
                    "mt-1.5 size-2 shrink-0 rounded-full",
                    DOT[n.type],
                  )}
                />
                <div className="flex flex-1 flex-col gap-0.5">
                  <span className="label-sm text-text-primary">{n.title}</span>
                  <span className="paragraph-sm text-text-tertiary">
                    {n.subtitle}
                  </span>
                </div>
                <span className="shrink-0 paragraph-sm text-text-secondary">
                  {n.time}
                </span>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
